import { Room } from "@/types/room";

// Types for our enriched marketing metadata
export type RoomTag = 
  | "ULTIMATE LUXURY" 
  | "RARE FIND" 
  | "RECOMMENDED" 
  | "GUEST FAVORITE" 
  | "VALUE CHOICE" 
  | "FAMILY FRIENDLY" 
  | "CLASSIC" 
  | "SOLO FAVORITE" 
  | "ENHANCED" 
  | "BUDGET"
  | "SPACE FOR ALL";

export type ViewBadgeType = 
  | "Eiger, Mönch & Jungfrau View" 
  | "Lake View" 
  | "Beatenberg View" 
  | "Village Side" 
  | "Village View" 
  | "Standard View";

export type FilterCategory = "All" | "Eiger, Mönch & Jungfrau View" | "Lake View" | "Beatenberg View" | "Balcony" | "No balcony" | "Suites";

export interface RoomMetadata {
  id: string; // Matches ROOMS_DATA id
  filterCategories: FilterCategory[];
  tag?: RoomTag;
  /** One or more view badges to display on the room card */
  viewBadges: ViewBadgeType[];
  descriptionHighlight: string;
  sortOrder: {
    default: number; // 2 adults
    single?: number; // 1 adult override
  };
}

// Map mapping room IDs to their marketing metadata
// Beatenberg View = village-side marketing (same chip as “village view” on cards).
export const ROOM_METADATA: Record<string, RoomMetadata> = {
  // Suites - Eiger, Mönch & Jungfrau View, Lake View, Beatenberg View (family-suite), Balcony, Suites
  "family-suite": {
    id: "family-suite",
    filterCategories: ["All", "Eiger, Mönch & Jungfrau View", "Lake View", "Beatenberg View", "Balcony", "Suites"],
    tag: "SPACE FOR ALL",
    viewBadges: ["Lake View", "Eiger, Mönch & Jungfrau View", "Beatenberg View"],
    descriptionHighlight: "Home-away-from-home with kitchenette & panoramic views.",
    sortOrder: { default: 10 } // Special case, usually filtered separate or at top
  },
  "junior-suite": {
    id: "junior-suite",
    filterCategories: ["All", "Eiger, Mönch & Jungfrau View", "Lake View", "Balcony", "Suites"],
    tag: "RARE FIND",
    viewBadges: ["Lake View", "Eiger, Mönch & Jungfrau View"],
    descriptionHighlight: "Separate living area with sophisticated luxury.",
    sortOrder: { default: 1 } // Anchor
  },

  // Double Rooms
  "superior-double-room": {
    id: "superior-double-room",
    filterCategories: ["All", "Eiger, Mönch & Jungfrau View", "Lake View", "Balcony"],
    tag: "RECOMMENDED",
    viewBadges: ["Lake View", "Eiger, Mönch & Jungfrau View"],
    descriptionHighlight: "Elevated experience with premium amenities.",
    sortOrder: { default: 2 } // Hero
  },
  "alpine-double-twin-balcony-lake": { // Double w/ Balcony (Jungfrau) - Alpine Double/Twin Mountain & Lake View Balcony
    id: "alpine-double-twin-balcony-lake",
    filterCategories: ["All", "Eiger, Mönch & Jungfrau View", "Lake View", "Balcony"],
    tag: "GUEST FAVORITE",
    viewBadges: ["Lake View", "Eiger, Mönch & Jungfrau View"],
    descriptionHighlight: "Wake up to iconic Jungfrau views from your balcony.",
    sortOrder: { default: 3, single: 2 } // Volume (Default), Upsell (Single)
  },
  "double-twin-balcony-lake": { // MEWS ladder alias - Alpine Double/Twin Mountain & Lake View Balcony
    id: "double-twin-balcony-lake",
    filterCategories: ["All", "Eiger, Mönch & Jungfrau View", "Lake View", "Balcony"],
    tag: "GUEST FAVORITE",
    viewBadges: ["Lake View", "Eiger, Mönch & Jungfrau View"],
    descriptionHighlight: "Wake up to iconic Jungfrau views from your balcony.",
    sortOrder: { default: 3, single: 2 }
  },
  "double-room-with-balcony": { // Double w/ Balcony (Village)
    id: "double-room-with-balcony",
    filterCategories: ["All", "Beatenberg View", "Balcony"],
    tag: "VALUE CHOICE",
    viewBadges: ["Beatenberg View"],
    descriptionHighlight: "Classic comfort with a charming village balcony.",
    sortOrder: { default: 4 } // Value
  },
  "double-twin-balcony-village": { // MEWS ladder alias - Alpine Double/Twin Village View Balcony
    id: "double-twin-balcony-village",
    filterCategories: ["All", "Beatenberg View", "Balcony"],
    tag: "CLASSIC",
    viewBadges: ["Beatenberg View"],
    descriptionHighlight: "Classic comfort with a charming village balcony.",
    sortOrder: { default: 4 }
  },
  "double-room": { // Double w/o Balcony
    id: "double-room",
    filterCategories: ["All", "No balcony"],
    tag: "CLASSIC",
    viewBadges: ["Standard View"],
    descriptionHighlight: "Comfortable retreat after a day of adventure.",
    sortOrder: { default: 6 } // Budget (after Triple in plan, but flexible)
  },
  "double-twin-noBal": { // MEWS ladder alias - Classic Double/Twin
    id: "double-twin-noBal",
    filterCategories: ["All", "Beatenberg View", "No balcony"],
    tag: "VALUE CHOICE",
    viewBadges: ["Beatenberg View"],
    descriptionHighlight: "Comfortable retreat after a day of adventure.",
    sortOrder: { default: 6 }
  },
  "twin-room": { // Mapped to Classic/Budget group
    id: "twin-room",
    filterCategories: ["All", "No balcony"],
    tag: "CLASSIC",
    viewBadges: ["Standard View"],
    descriptionHighlight: "Comfortable twin room with flexible options.",
    sortOrder: { default: 7 }
  },
  "twin-room-with-balcony": {
    id: "twin-room-with-balcony",
    filterCategories: ["All", "Beatenberg View", "Balcony"],
    tag: "VALUE CHOICE",
    viewBadges: ["Beatenberg View"],
    descriptionHighlight: "Classic twin comfort with balcony.",
    sortOrder: { default: 5 }
  },
  "alpine-classic-double-twin-village": {
     id: "alpine-classic-double-twin-village",
     filterCategories: ["All", "Beatenberg View", "Balcony"],
     tag: "VALUE CHOICE",
     viewBadges: ["Beatenberg View"],
     descriptionHighlight: "Classic comfort with village views.",
     sortOrder: { default: 4.5 }
  },

  // Triple
  "standard-triple-room": {
    id: "standard-triple-room",
    filterCategories: ["All", "Beatenberg View", "Balcony"],
    tag: "FAMILY FRIENDLY",
    viewBadges: ["Beatenberg View"],
    descriptionHighlight: "Spacious & flexible for small groups.",
    sortOrder: { default: 5 } // Budget/Volume
  },

  // Singles — Jungfrau single (ladder: single-balcony-lake) stays under Eiger/Mönch/Jungfrau + Lake + Balcony
  "jungfrau-single-balcony-lake": { // Single w/ Balcony (Jungfrau)
    id: "jungfrau-single-balcony-lake",
    filterCategories: ["All", "Eiger, Mönch & Jungfrau View", "Lake View", "Balcony"],
    tag: "SOLO FAVORITE",
    viewBadges: ["Lake View", "Eiger, Mönch & Jungfrau View"],
    descriptionHighlight: "Your private balcony with majestic views.",
    sortOrder: { default: 8, single: 1 } // Hero for 1 adult
  },
  "single-balcony-lake": { // MEWS ladder alias - Jungfrau Single
    id: "single-balcony-lake",
    filterCategories: ["All", "Eiger, Mönch & Jungfrau View", "Lake View", "Balcony"],
    tag: "SOLO FAVORITE",
    viewBadges: ["Lake View", "Eiger, Mönch & Jungfrau View"],
    descriptionHighlight: "Your private balcony with majestic views.",
    sortOrder: { default: 8, single: 1 }
  },
  "single-with-balcony": { // Alternate ID for Single w/ Balcony (Jungfrau)
      id: "single-with-balcony",
      filterCategories: ["All", "Eiger, Mönch & Jungfrau View", "Lake View", "Balcony"],
      tag: "SOLO FAVORITE",
      viewBadges: ["Lake View", "Eiger, Mönch & Jungfrau View"],
      descriptionHighlight: "Your private balcony with majestic views.",
      sortOrder: { default: 8, single: 1 }
  },
  "standard-single": {
    id: "standard-single",
    filterCategories: ["All", "No balcony"],
    tag: "ENHANCED",
    viewBadges: ["Standard View"],
    descriptionHighlight: "Extra space & comfort for solo stays.",
    sortOrder: { default: 9, single: 3 } // Value for 1 adult
  },
  "heritage-single": { // MEWS ladder - Heritage Single Village View (= Beatenberg)
    id: "heritage-single",
    filterCategories: ["All", "Beatenberg View", "No balcony"],
    tag: "ENHANCED",
    viewBadges: ["Beatenberg View"],
    descriptionHighlight: "Extra space & comfort for solo stays.",
    sortOrder: { default: 9, single: 3 }
  },
  "basic-single": {
    id: "basic-single",
    filterCategories: ["All", "Beatenberg View", "No balcony"],
    tag: "BUDGET",
    viewBadges: ["Beatenberg View"],
    descriptionHighlight: "Essential comfort for the active traveler.",
    sortOrder: { default: 10, single: 4 } // Budget for 1 adult
  },
  "heritage-single-village": {
      id: "heritage-single-village",
      filterCategories: ["All", "Beatenberg View", "No balcony"],
      tag: "ENHANCED",
      viewBadges: ["Beatenberg View"],
      descriptionHighlight: "Heritage charm with village views.",
      sortOrder: { default: 9.5, single: 3.5 }
  }
};

export const getRoomMetadata = (id: string): RoomMetadata | undefined => {
  return ROOM_METADATA[id];
};
